import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { pushEvent } from "@/lib/gtm";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function VerifyEmailPage() {
  usePageTitle("Verify Email");
  const [, navigate] = useLocation();

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setStatus("error");
      setErrorMessage("No verification token provided.");
      return;
    }

    const returnTo = params.get("returnTo");
    const safeReturnTo =
      returnTo &&
      returnTo.startsWith("/") &&
      !returnTo.startsWith("//") &&
      !returnTo.includes("..")
        ? returnTo
        : null;

    fetch(`${BASE}/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
      credentials: "include",
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (res.ok && data?.ok) {
          setStatus("success");
          if (data.signupEventId) {
            pushEvent("SignUp", { event_id: data.signupEventId });
            pushEvent("sign_up", { method: "email", event_id: data.signupEventId });
          }
          setTimeout(() => {
            window.location.href = safeReturnTo ? `${BASE}${safeReturnTo}` : BASE || "/";
          }, 2000);
        } else {
          setStatus("error");
          setErrorMessage(data?.error || "Verification failed. Please try again.");
        }
      })
      .catch(() => {
        setStatus("error");
        setErrorMessage("Something went wrong. Please try again.");
      });
  }, []);

  return (
    <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          {status === "loading" && (
            <>
              <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-blue-50">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <CardTitle className="text-2xl">Verifying your email…</CardTitle>
              <CardDescription>Please wait a moment.</CardDescription>
            </>
          )}
          {status === "success" && (
            <>
              <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-green-50">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Email verified!</CardTitle>
              <CardDescription>Your account is now active. Redirecting you to the app…</CardDescription>
            </>
          )}
          {status === "error" && (
            <>
              <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl">Verification failed</CardTitle>
              <CardDescription>{errorMessage}</CardDescription>
            </>
          )}
        </CardHeader>
        {status === "error" && (
          <CardContent className="space-y-3">
            <Button className="w-full" onClick={() => navigate("/login")}>
              Back to sign in
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
    </>
  );
}
